import React, { useState, useEffect } from 'react';
import {
    ExclamationCircleFilled,
    PlusOutlined, LoadingOutlined 
} from '@ant-design/icons';
import {
    Button, Col, DatePicker, Divider, Drawer,
    Empty, Form, Input, InputNumber, Modal,
    Row, Select, Space, Table, Typography, message, Spin 
} from 'antd';
import { 
    insert as insertCredito,
        update as updateCredito,
    getById as getCreditoById,
  } from '../../../../../apis/CreditoInfonavitApi';
  import { 
    getAmortizacionesByCredito,
    createAmortizacion,
        updateAmortizacion,
    deleteAmortizacion
  } from '../../../../../apis/CreditoInfonavitApi';
import {
    getList as getAllTiposSaldo
} from '../../../../../apis/TipoSaldoApi';
import {
    getEmpleados
} from '../../../../../apis/OdooApi';
import './styles.scss';
import dayjs from 'dayjs';

const { Option } = Select;
const { confirm } = Modal;
const { Title } = Typography;

const formItemLayout = {
    labelCol: { span: 6 },
    wrapperCol: { span: 18 },
};

const App = ({ title, empleadoId, id, open, finish }) => {
    const [form] = Form.useForm();
    const [amortizacionForm] = Form.useForm();
    const [ID, setID] = useState(id);
    const [openAmortizacion, setOpenAmortizacion] = useState(false);
    const [amortizaciones, setAmortizaciones] = useState([]);
    const [currentAmortizacion, setCurrentAmortizacion] = useState(null);
    const [loading, setLoading] = useState(false);
    const [tiposSaldo, setTiposSaldo] = useState([]);
    const [empleados, setEmpleados] = useState([]);
    const [loadingEmpleados, setLoadingEmpleados] = useState(false);
    const [loadingTiposSaldo, setLoadingTiposSaldo] = useState(false);
    const [initialLoadComplete, setInitialLoadComplete] = useState(false);

    useEffect(() => {
        if (open && !initialLoadComplete) {
          const loadInitialData = async () => {
            await Promise.all([
              getTiposSaldos(),
              getEmpleadosOdoo()
            ]);
            setInitialLoadComplete(true);
            await fetchData();
          };
          
          loadInitialData();
        }
      }, [open]);

    const handleSubmit = async () => {
        try {
            setLoading(true);
            const values = await form.validateFields();

            // Formatear fechas correctamente
            const formatDate = (date) => date ? date.format('YYYY-MM-DD') : null;

            const payload = {
                empleado_id: values.empleado,
                tipo_saldo: values.tipo_saldo.toString(),
                tipo_credito: parseInt(values.tipo_credito),
                fecha_inicio: formatDate(values.fecha_inicio),
                fecha_suspension: formatDate(values.fecha_suspension),
                monto_inicial: values.monto_inicial,
                numero_credito: values.numero_credito,
                saldo: values.saldo
            };

            if (ID > 0) {
                // Actualizar crédito existente
                const response = await updateCredito({
                    id: ID,
                    ...payload
                });
                if (response.ok) {
                    message.success('Crédito actualizado correctamente');
                    finish(true);
                }
            } else {
                // Crear nuevo crédito
                const response = await insertCredito(payload);
                if (response.ok) {
                    setID(response.payload?.id || 0);
                    message.success('Crédito creado correctamente');
                    finish(true);
                }
            }
        } catch (error) {
            console.error('Error:', error);
            message.error('Ocurrió un error al guardar el crédito');
        } finally {
            setLoading(false);
        }
    };

    const handleAmortizacionSubmit = async () => {
        try {
            setLoading(true);
            const values = await amortizacionForm.validateFields();

            const payload = {
                credito_id: ID,
                fecha_amortizacion: values.fecha_amortizacion.format('YYYY-MM-DD'),
                monto: values.monto,
                referencia: values.referencia || null
            };

            if (currentAmortizacion) {
                // Actualizar amortización existente
                const response = await updateAmortizacion({
                    id: currentAmortizacion.id,
                    ...payload
                });
                if (response.ok) {
                    message.success('Amortización actualizada correctamente');
                    setOpenAmortizacion(false);
                    await fetchAmortizaciones(ID);
                }
            } else {
                // Crear nueva amortización
                const response = await createAmortizacion(payload);
                if (response.ok) {
                    message.success('Amortización creada correctamente');
                    setOpenAmortizacion(false);
                    await fetchAmortizaciones(ID);
                }
            }
        } catch (error) {
            console.error('Error:', error);
            message.error('Ocurrió un error al guardar la amortización');
        } finally {
            setLoading(false);
        }
    };

    const fetchAmortizaciones = async (creditoId) => {
        try {
            const response = await getAmortizacionesByCredito(creditoId);
            if (response.ok) {
                setAmortizaciones(response.payload);
            }
        } catch (error) {
            console.error('Error fetching amortizaciones:', error);
            message.error('Error al cargar las amortizaciones');
        }
    };

    const fetchData = async () => {
        if (ID > 0) {
            try {
                const response = await getCreditoById(ID);
                if (response.ok) {
                    const credito = response.payload;

                    // Convertir fechas string a objetos dayjs para el formulario
                    const fechaInicio = credito.fecha_inicio ? dayjs(credito.fecha_inicio) : null;
                    const fechaSuspension = credito.fecha_suspension ? dayjs(credito.fecha_suspension) : null;

                    form.setFieldsValue({
                        ...credito,
                        empleado: credito.empleado_id,
                        fecha_inicio: fechaInicio,
                        fecha_suspension: fechaSuspension
                    });
                    await fetchAmortizaciones(ID);
                }
            } catch (error) {
                message.error('Error al cargar los datos del crédito');
            }
        } else {
            form.resetFields();
            setAmortizaciones([]);

            // Establecer valores iniciales para nuevo crédito
            form.setFieldsValue({
                empleado: empleadoId,
                numero_credito: generarNumeroCredito(empleadoId)
            });
        }
    };

    const handleDeleteAmortizacion = (id) => {
        confirm({
            title: '¿Estás seguro de eliminar esta amortización?',
            icon: <ExclamationCircleFilled />,
            content: 'Esta acción no se puede deshacer',
            async onOk() {
                try {
                    const response = await deleteAmortizacion(id);
                    if (response.ok) {
                        message.success('Amortización eliminada correctamente');
                        await fetchAmortizaciones(ID);
                    }
                } catch (error) {
                    console.error('Error deleting:', error);
                    message.error('Error al eliminar la amortización');
                }
            }
        });
    };

    const getTiposSaldos = async () => {
        try {
          setLoadingTiposSaldo(true);
          const response = await getAllTiposSaldo();
          if(response.ok) {
            setTiposSaldo(response.payload);
          }
        } catch (error) {
          console.error('Error obteniendo tipos de saldo:', error);
          message.error('Error al cargar tipos de saldo');
        } finally {
          setLoadingTiposSaldo(false);
        }
      };

      const getEmpleadosOdoo = async () => {
        try {
          setLoadingEmpleados(true);
          const response = await getEmpleados();
          if(response.ok) {
            setEmpleados(response.payload);
          }
        } catch (error) {
          console.error('Error obteniendo empleados:', error);
          message.error('Error al cargar lista de empleados');
        } finally {
          setLoadingEmpleados(false);
        }
      };

    const onChangeEmpleado = (value) => {
        const fields = form.getFieldsValue();
        fields['numero_credito'] = generarNumeroCredito(value);
        form.setFieldsValue(fields);
    };

    function generarNumeroCredito(empleadoId) {
        if (!empleadoId) return ''; // O algún valor por defecto
        
        // Forzar el número a 5 dígitos (rellenando con ceros a la izquierda)
        const numeroFormateado = empleadoId.toString().padStart(5, '0');
        
        // Obtener la fecha actual
        const fecha = new Date();
        const anio = fecha.getFullYear();
        const mes = (fecha.getMonth() + 1).toString().padStart(2, '0'); // Mes es base 0
        const dia = fecha.getDate().toString().padStart(2, '0');
        
        // Concatenar en el formato deseado
        return `${numeroFormateado}${anio}${mes}${dia}`;
      }

    useEffect(() => {
        if (open) {
            getTiposSaldos();
            getEmpleadosOdoo();
            fetchData();
        }
    }, [open, ID]);

    const columns = [
        {
          title: 'Fecha Amortización',
          dataIndex: 'fecha_amortizacion',
          key: 'fecha_amortizacion',
          render: (date) => new Date(date).toLocaleDateString()
        },
        {
          title: 'Monto',
          dataIndex: 'monto',  // Cambiado de 'amortizacion' a 'monto'
          key: 'monto',
          render: (value) => typeof value === 'number' ? value.toFixed(4) : 'N/A'
        },
        {
          title: 'Referencia',
          dataIndex: 'referencia',
          key: 'referencia',
          ellipsis: true
        },
        {
          title: 'Acciones',
          key: 'actions',
          render: (_, record) => (
            <Space>
              <Button size="small" onClick={() => {
                setCurrentAmortizacion(record);
                amortizacionForm.setFieldsValue({
                  ...record,
                  fecha_amortizacion: dayjs(record.fecha_amortizacion)
                });
                setOpenAmortizacion(true);
              }}>Editar</Button>
              <Button size="small" danger onClick={() => handleDeleteAmortizacion(record.id)}>Eliminar</Button>
            </Space>
          ),
        },
      ];

    return (
        <Drawer
            title={`${ID > 0 ? `(${ID}) ` : ''}${title}`}
            width={'80%'}
            placement="right"
            onClose={() => finish(false)}
            open={open}
            closable
            extra={[
                <Space>
                    <Button onClick={() => finish(false)}>Cancelar</Button>
                    <Button type="primary" loading={loading} onClick={handleSubmit}>
                        Guardar
                    </Button>
                </Space>
            ]}
        >
                  {(!initialLoadComplete || loadingEmpleados || loadingTiposSaldo) ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <Spin 
            indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} 
            tip="Cargando datos iniciales..."
          />
        </div>
      ) : (
        <>
            <Form form={form} layout="vertical" {...formItemLayout}>
                <Row gutter={16}>
                    {empleados && empleados.length > 0 && (
                        <Col md={12} sm={12} xs={24}>
                            <Form.Item
                                name="empleado"
                                label="Empleado"
                                rules={[{ required: true, message: 'Por favor selecciona el empleado' }]}
                            >
                                <Select
                                    placeholder="Selecciona el empleado"
                                    onChange={onChangeEmpleado}
                                    showSearch
                                    filterOption={(input, option) =>
                                        (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                                    }
                                >
                                    {empleados.map((item) => (
                                        <Option value={item.value} key={item.value}>{item.text}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    )}

                    {tiposSaldo && tiposSaldo.length > 0 && (
                        <Col md={12} sm={12} xs={24}>
                            <Form.Item
                                name="tipo_saldo"
                                label="Tipo Saldo"
                                rules={[{ required: true, message: 'Por favor selecciona el concepto' }]}
                            >
                                <Select
                                    placeholder="Selecciona el tipo de saldo"
                                    showSearch
                                    filterOption={(input, option) =>
                                        (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                                    }
                                >
                                    {tiposSaldo.map((item) => (
                                        <Option value={String(item.value)} key={item.value}>{item.displayTitle}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    )}

                    <Col md={12} sm={12} xs={24}>
                        <Form.Item
                            name="tipo_credito"
                            label="Tipo Crédito"
                            rules={[{ required: true, message: 'Por favor selecciona el tipo de crédito' }]}
                        >
                            <Select placeholder="Selecciona el tipo de crédito">
                                <Option value={0}>Ninguno</Option>
                                <Option value={1}>Porcentaje</Option>
                                <Option value={2}>Cuota</Option>
                                <Option value={3}>Veces SM</Option>
                            </Select>
                        </Form.Item>
                    </Col>

                    <Col md={12} sm={12} xs={24}>
                        <Form.Item
                            name="fecha_inicio"
                            label="Fecha de Inicio"
                            rules={[{ required: true, message: 'Por favor agrega una fecha' }]}
                        >
                            <DatePicker
                                placeholder="DD/MM/YYYY"
                                style={{ width: '100%' }}
                                format={'DD/MM/YYYY'}
                            />
                        </Form.Item>
                    </Col>

                    <Col md={12} sm={12} xs={24}>
                        <Form.Item
                            name="fecha_suspension"
                            label="Fecha Suspensión"
                        >
                            <DatePicker
                                placeholder="DD/MM/YYYY"
                                style={{ width: '100%' }}
                                format={'DD/MM/YYYY'}
                            />
                        </Form.Item>
                    </Col>

                    <Col md={12} sm={12} xs={24}>
                        <Form.Item
                            name="monto_inicial"
                            label="Monto Inicial"
                            rules={[{ required: true, message: 'Por favor agrega el monto' }]}
                        >
                            <InputNumber
                                placeholder="Monto inicial"
                                style={{ width: '100%' }}
                            />
                        </Form.Item>
                    </Col>

                    <Col md={12} sm={12} xs={24}>
                        <Form.Item
                            name="numero_credito"
                            label="Número de Crédito"
                        >
                            <Input style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>

                    <Col md={12} sm={12} xs={24}>
                        <Form.Item
                            name="saldo"
                            label="Saldo"
                            rules={[{ required: true, message: 'Por favor agrega el monto' }]}
                        >
                            <InputNumber style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                </Row>

                <Divider orientation="left">Historial de Amortizaciones</Divider>

                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => {
                        setCurrentAmortizacion(null);
                        amortizacionForm.resetFields();
                        setOpenAmortizacion(true);
                    }}
                    disabled={!ID}
                    style={{ marginBottom: 16 }}
                >
                    Agregar Amortización
                </Button>

                <Table
                    columns={columns}
                    dataSource={amortizaciones}
                    rowKey="id"
                    locale={{
                        emptyText: <Empty description="No hay amortizaciones registradas" />,
                    }}
                    loading={loading}
                />
            </Form>

            {/* Drawer para Amortización */}
            <Drawer
                title={currentAmortizacion ? 'Editar Amortización' : 'Agregar Amortización'}
                width={600}
                onClose={() => {
                    setOpenAmortizacion(false);
                    setCurrentAmortizacion(null);
                }}
                open={openAmortizacion}
                destroyOnClose
                extra={[
                    <Space>
                        <Button onClick={() => setOpenAmortizacion(false)}>Cancelar</Button>
                        <Button type="primary" loading={loading} onClick={handleAmortizacionSubmit}>
                            Guardar
                        </Button>
                    </Space>
                ]}
            >
                <Form form={amortizacionForm} layout="vertical">
                    <Form.Item
                        name="fecha_amortizacion"
                        label="Fecha Amortización"
                        rules={[{ required: true, message: 'Campo requerido' }]}
                    >
                        <DatePicker
                            style={{ width: '100%' }}
                            format={'DD/MM/YYYY'}
                        />
                    </Form.Item>

                    <Form.Item
                        name="monto"
                        label="Amortización"
                        rules={[{
                            required: true,
                            message: 'Campo requerido',
                            type: 'number',
                            min: 0,
                        }]}
                    >
                        <InputNumber
                            style={{ width: '100%' }}
                            step={0.0001}
                            precision={4}
                        />
                    </Form.Item>

                    <Form.Item
                        name="referencia"
                        label="Referencia"
                        rules={[
                            { max: 40, message: 'Máximo 40 caracteres' }
                        ]}
                    >
                        <Input
                            placeholder="Información adicional"
                            maxLength={40}
                            showCount
                        />
                    </Form.Item>
                </Form>
            </Drawer>
            </>
      )}
        </Drawer>
    );
};

export default App;